/**
 * Format an ISO8601 timestamp as a short "X ago" string, suitable for
 * "Last seen N minutes ago" rendering. Uses `Intl.RelativeTimeFormat`
 * (BCP47-aware) so localized strings come from the platform.
 *
 *   formatLastSeen("2026-06-25T18:30:00Z") // "yesterday"
 *   formatLastSeen("2026-06-26T09:55:00Z") // "5 minutes ago"
 *   formatLastSeen(null)                   // "" — caller decides UX
 */

const cache = new Map<string, Intl.RelativeTimeFormat>()

function rtfFor(locale: string): Intl.RelativeTimeFormat {
  let f = cache.get(locale)
  if (!f) {
    f = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
    cache.set(locale, f)
  }
  return f
}

export function formatLastSeen(iso: string | null, now: number = Date.now(), locale = 'en'): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const diffSec = Math.round((t - now) / 1000) // negative when in the past

  const abs = Math.abs(diffSec)
  const rtf = rtfFor(locale)

  if (abs < 60)        return rtf.format(Math.round(diffSec), 'second')
  if (abs < 3600)      return rtf.format(Math.round(diffSec / 60), 'minute')
  if (abs < 86400)     return rtf.format(Math.round(diffSec / 3600), 'hour')
  if (abs < 86400 * 7) return rtf.format(Math.round(diffSec / 86400), 'day')
  if (abs < 86400 * 30) return rtf.format(Math.round(diffSec / 86400 / 7), 'week')
  if (abs < 86400 * 365) return rtf.format(Math.round(diffSec / 86400 / 30), 'month')
  return rtf.format(Math.round(diffSec / 86400 / 365), 'year')
}
